import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Save, RotateCcw, Eye, EyeOff, CheckCircle, AlertCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Header } from "./Schools";

const PROVIDER_PRESETS = {
  gmail:     { smtp_host: "smtp.gmail.com",     smtp_port: 587, encryption: "tls" },
  hostinger: { smtp_host: "smtp.hostinger.com", smtp_port: 587, encryption: "tls" },
  custom:    { smtp_host: "",                   smtp_port: 587, encryption: "tls" },
};

const DEFAULTS = {
  sender_name: "",
  sender_email: "",
  smtp_provider: "gmail",
  smtp_host: "smtp.gmail.com",
  smtp_port: 587,
  smtp_username: "",
  smtp_password: "",
  encryption: "tls",
  reply_to_email: "",
  reply_to_name: "",
  enable_auth: true,
  enable_email_sending: false,
  enable_certificate_emails: true,
  enable_registration_emails: true,
  enable_feedback_emails: true,
};

/* ── tiny UI primitives ─────────────────────────────────────────────────── */
function Label({ children, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-700 mb-1">
      {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
    </label>
  );
}

function TextInput({ type = "text", ...rest }) {
  return (
    <input
      type={type}
      {...rest}
      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-400/30 focus:border-indigo-400 transition bg-white"
    />
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer py-2.5 select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-400/40 ${checked ? "bg-indigo-600" : "bg-gray-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
      </button>
      <span>
        <span className="block text-sm font-medium text-gray-800">{label}</span>
        {description && <span className="block text-xs text-gray-500 mt-0.5">{description}</span>}
      </span>
    </label>
  );
}

function RadioGroup({ name, value, options, onChange }) {
  return (
    <div className="flex flex-wrap gap-4">
      {options.map(opt => (
        <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="w-4 h-4 accent-indigo-600"
          />
          <span className="text-sm text-gray-700">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
      {title && (
        <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/70">
          <span className="text-sm font-semibold text-gray-700">{title}</span>
        </div>
      )}
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

/* ── main component ─────────────────────────────────────────────────────── */
export default function EmailIntegration() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testResult, setTestResult] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-settings");
      setForm({ ...DEFAULTS, ...data });
    } catch {
      toast.error("Could not load email settings");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
  }

  function onProvider(provider) {
    const preset = PROVIDER_PRESETS[provider] || PROVIDER_PRESETS.custom;
    setForm(prev => ({ ...prev, smtp_provider: provider, ...preset }));
  }

  async function save() {
    // client-side validation
    if (form.enable_email_sending) {
      if (!form.sender_email.trim()) { toast.error("Sender Email is required"); return; }
      if (!form.smtp_host.trim())    { toast.error("SMTP Host is required"); return; }
      if (!form.smtp_port)           { toast.error("SMTP Port is required"); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/email-settings", form);
      setForm({ ...DEFAULTS, ...data });
      toast.success("Email settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!testEmail.trim()) { toast.error("Enter a test recipient email"); return; }
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/email-settings/test", { test_recipient: testEmail.trim() });
      setTestResult({ ok: true, msg: data.message || "Test email sent successfully" });
    } catch (err) {
      setTestResult({ ok: false, msg: err?.response?.data?.detail || "Test failed" });
    } finally {
      setTesting(false);
    }
  }

  function reset() {
    if (window.confirm("Discard unsaved changes and reload saved settings?")) load();
  }

  /* ── render ─────────────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="fade-up">
        <Header title="Email Integration" subtitle="Configure SMTP for outbound emails." />
        <div className="mt-6 space-y-4 max-w-2xl">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="h-36 rounded-2xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="fade-up pb-12">
      <Header
        title="Email Integration"
        subtitle="Configure Gmail or Hostinger SMTP to deliver certificates, registrations, and feedback emails."
        actions={
          <div className="flex flex-wrap gap-2">
            <button
              onClick={reset}
              className="inline-flex items-center gap-1.5 border border-gray-200 bg-white text-gray-700 text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition"
            >
              <RotateCcw className="w-4 h-4" /> Reset
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-60"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
            </button>
          </div>
        }
      />

      <div className="mt-6 space-y-5 max-w-2xl">

        {/* ── Master switch ── */}
        <Card title="Email Sending">
          <Toggle
            label="Enable Email Sending"
            description="Master switch — all outbound emails are suppressed when off."
            checked={!!form.enable_email_sending}
            onChange={v => set("enable_email_sending", v)}
          />
          {!form.enable_email_sending && (
            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              Email sending is currently disabled. Configure SMTP below, then enable this switch and save.
            </div>
          )}
        </Card>

        {/* ── Sender details ── */}
        <Card title="Sender Details">
          <Row>
            <div>
              <Label required>Sender Name</Label>
              <TextInput
                placeholder="Manthan Foundation"
                value={form.sender_name}
                onChange={e => set("sender_name", e.target.value)}
              />
            </div>
            <div>
              <Label required>Sender Email</Label>
              <TextInput
                type="email"
                placeholder="noreply@manthanfoundation.in"
                value={form.sender_email}
                onChange={e => set("sender_email", e.target.value)}
              />
            </div>
            <div>
              <Label>Reply-To Email</Label>
              <TextInput
                type="email"
                placeholder="info@manthanfoundation.in"
                value={form.reply_to_email}
                onChange={e => set("reply_to_email", e.target.value)}
              />
            </div>
            <div>
              <Label>Reply-To Name</Label>
              <TextInput
                placeholder="Support Team"
                value={form.reply_to_name}
                onChange={e => set("reply_to_name", e.target.value)}
              />
            </div>
          </Row>
        </Card>

        {/* ── Provider ── */}
        <Card title="SMTP Provider">
          <RadioGroup
            name="smtp_provider"
            value={form.smtp_provider}
            onChange={onProvider}
            options={[
              { value: "gmail",     label: "Gmail" },
              { value: "hostinger", label: "Hostinger" },
              { value: "custom",    label: "Custom / Other" },
            ]}
          />
          {form.smtp_provider === "gmail" && (
            <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5 space-y-1">
              <p className="font-semibold">Gmail requires an App Password</p>
              <p>Go to <strong>Google Account → Security → 2-Step Verification → App Passwords</strong> and generate a password for "Mail".</p>
              <p>Use that App Password below — not your regular Google account password.</p>
            </div>
          )}
        </Card>

        {/* ── SMTP connection ── */}
        <Card title="SMTP Connection">
          <Row>
            <div>
              <Label required>SMTP Host</Label>
              <TextInput
                placeholder="smtp.gmail.com"
                value={form.smtp_host}
                onChange={e => set("smtp_host", e.target.value)}
              />
            </div>
            <div>
              <Label required>SMTP Port</Label>
              <TextInput
                type="number"
                placeholder="587"
                value={form.smtp_port}
                onChange={e => set("smtp_port", parseInt(e.target.value) || 587)}
              />
            </div>
          </Row>

          <div>
            <Label>Encryption</Label>
            <div className="mt-1">
              <RadioGroup
                name="encryption"
                value={form.encryption}
                onChange={v => set("encryption", v)}
                options={[
                  { value: "tls",      label: "TLS (port 587 — recommended)" },
                  { value: "ssl",      label: "SSL (port 465)" },
                  { value: "starttls", label: "STARTTLS" },
                ]}
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <Toggle
              label="Enable Authentication"
              description="Send username and password to the SMTP server."
              checked={!!form.enable_auth}
              onChange={v => set("enable_auth", v)}
            />
          </div>

          {form.enable_auth && (
            <Row>
              <div>
                <Label required>SMTP Username</Label>
                <TextInput
                  placeholder="your@email.com"
                  value={form.smtp_username}
                  onChange={e => set("smtp_username", e.target.value)}
                />
              </div>
              <div>
                <Label required>SMTP Password</Label>
                <div className="relative">
                  <TextInput
                    type={showPwd ? "text" : "password"}
                    placeholder="App password or SMTP password"
                    value={form.smtp_password}
                    onChange={e => set("smtp_password", e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Never shown again after saving.</p>
              </div>
            </Row>
          )}
        </Card>

        {/* ── Email types ── */}
        <Card title="Email Types">
          <div className="divide-y divide-gray-100 -my-1">
            <Toggle
              label="Certificate Emails"
              description="Attach and email the certificate PDF to teachers after approval."
              checked={!!form.enable_certificate_emails}
              onChange={v => set("enable_certificate_emails", v)}
            />
            <Toggle
              label="Registration Emails"
              description="Notify teachers when their training registration is approved, rejected, or needs changes."
              checked={!!form.enable_registration_emails}
              onChange={v => set("enable_registration_emails", v)}
            />
            <Toggle
              label="Feedback Emails"
              description="Send feedback request and review notification emails."
              checked={!!form.enable_feedback_emails}
              onChange={v => set("enable_feedback_emails", v)}
            />
          </div>
        </Card>

        {/* ── Test email ── */}
        <Card title="Test Email">
          <p className="text-xs text-gray-500">
            Send a test email using the <strong>currently saved</strong> SMTP settings.
            Save your configuration first, then test.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <TextInput
              type="email"
              placeholder="your@email.com"
              value={testEmail}
              onChange={e => { setTestEmail(e.target.value); setTestResult(null); }}
            />
            <button
              onClick={sendTest}
              disabled={testing || !form.enable_email_sending}
              className="sm:shrink-0 inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition disabled:opacity-50"
            >
              <Mail className="w-4 h-4" />
              {testing ? "Sending…" : "Send Test Email"}
            </button>
          </div>

          {!form.enable_email_sending && (
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              Enable email sending and save before testing.
            </p>
          )}

          {testResult && (
            <div className={`flex items-start gap-2.5 rounded-lg border px-3 py-3 text-sm ${
              testResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-rose-50 border-rose-200 text-rose-700"
            }`}>
              {testResult.ok
                ? <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
                : <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              }
              {testResult.msg}
            </div>
          )}
        </Card>

        {/* ── Bottom actions ── */}
        <div className="flex flex-col sm:flex-row gap-3 pt-1">
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1e293b] text-white text-sm font-semibold px-6 py-2.5 rounded-lg transition disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Settings"}
          </button>
          <button
            onClick={reset}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium px-6 py-2.5 rounded-lg transition"
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
        </div>

      </div>
    </div>
  );
}
